import React, { useEffect, useMemo, useState } from 'react'
import { useFileTypeIcon } from 'src/components/ModalForms/FileType/useFileTypeIcon'
import { CsvDataGridContainer } from 'src/components/ModalForms/Evidence/evidence.styles'
import {
  StyledCsvBody,
  StyledCsvModal,
} from 'src/pages/Evidence/evidence.styles'
import { getFileType } from 'src/components/ModalForms/Evidence/ViewEvidenceForm/ViewEvidenceForm.utils'
import {
  EvidenceExcelSheet,
  getEvidenceCsv,
  getEvidenceExcel,
} from 'src/Utils/Evidence/evidence.helpers'
import log from 'loglevel'
import { GridInitialState } from '@mui/x-data-grid'
import { EvidenceDataGrid } from 'src/pages/Evidence/show/tabs/evidence/AutomaticEvidence/AutomaticEvidenceBody'
import { NoResults } from 'src/components/PageLayout/NoResults'
import { Tabs } from 'src/components/Reusable/Tabs/Tabs'
import { TextButton } from '../Buttons/TextButton'
import {
  ExpandButtonRow,
  PreviewDiv,
  SheetFrame,
  SheetMessageFrame,
  SheetPreamble,
  StyledCloseButton,
  StyledEvidenceIframe,
  StyledEvidenceImage,
  StyledImagePreview,
  StyledPreviewModal,
  StyledPreviewModalIframe,
  StyledPreviewModalImage,
} from './DocumentViewer.styles'
import { useToggleDocumentViewer } from './DocumentViewer.hooks'

// An uploaded table opens in the file's own row order; sorting is a click away.
const FILE_ORDER: GridInitialState = { sorting: { sortModel: [] } }

export const InlineImage = ({
  file,
  isEvidenceShowPage,
  link,
  isModalOpen,
  toggleModal,
}: {
  file: File
  isEvidenceShowPage?: boolean
  link: string
  isModalOpen: boolean
  toggleModal: () => void
}): JSX.Element => (
  <>
    {isEvidenceShowPage ? (
      <StyledEvidenceImage
        src={link as string}
        alt={file.name}
        onClick={toggleModal}
      />
    ) : (
      <PreviewDiv>
        <StyledImagePreview
          src={link as string}
          alt="Preview"
          onClick={toggleModal}
        />
      </PreviewDiv>
    )}
    {isModalOpen && (
      <StyledPreviewModal onClick={toggleModal}>
        <StyledPreviewModalImage src={link as string} alt="Expanded Preview" />
        <CloseButton
          onClick={(e: React.MouseEvent<HTMLElement>) => {
            e.preventDefault()
            e.stopPropagation()
            toggleModal()
          }}
        />
      </StyledPreviewModal>
    )}
  </>
)

export const InlinePdf = ({
  link: originalLink,
  isEvidenceShowPage,
  isModalOpen,
  toggleModal,
  pageNumber,
}: {
  link: string
  isEvidenceShowPage?: boolean
  isModalOpen: boolean
  toggleModal: () => void
  pageNumber?: number
}): JSX.Element => {
  const FileTypeIcon = useFileTypeIcon({ mime: 'application/pdf' })

  const link = useMemo(() => {
    if (pageNumber) {
      return `${originalLink}#page=${pageNumber}`
    }
    return originalLink
  }, [originalLink, pageNumber])

  return (
    <>
      {isEvidenceShowPage ? (
        <StyledEvidenceIframe src={link as string} typeof="application/pdf" />
      ) : (
        <PreviewDiv onClick={toggleModal}>
          <FileTypeIcon width="120px" height="120px" />
          <p>View PDF</p>
        </PreviewDiv>
      )}
      {isModalOpen && (
        <StyledPreviewModal onClick={toggleModal}>
          <StyledPreviewModalIframe
            src={link as string}
            typeof="application/pdf"
          />
          <CloseButton />
        </StyledPreviewModal>
      )}
    </>
  )
}

export const InlineCsv = ({
  body,
  isModalOpen,
  toggleModal,
  isModal,
}: {
  body?: string | Uint8Array
  isModalOpen: boolean
  toggleModal: () => void
  isModal?: boolean
}): JSX.Element => {
  if (!body) return <></>
  // get rows and columns from csv file
  const { rows, columns } = getEvidenceCsv(body)

  return (
    <>
      <CsvDataGridContainer $isModal={isModal}>
        <EvidenceDataGrid
          rows={rows}
          columns={columns}
          initialState={FILE_ORDER}
        />
      </CsvDataGridContainer>
      {isModalOpen && (
        <CsvPreviewModal
          body={
            <EvidenceDataGrid
              rows={rows}
              columns={columns}
              initialState={FILE_ORDER}
            />
          }
          onClose={toggleModal}
        />
      )}
    </>
  )
}

const ExcelSheet = ({
  sheet,
  downloaderComponent,
}: {
  sheet: EvidenceExcelSheet
  downloaderComponent: JSX.Element
}): JSX.Element => {
  if (sheet.grid?.columns.length) {
    // A column, so the caption sits above the grid wherever the sheet is
    // framed (the expanded view's frame lays its children out in a row).
    return (
      <SheetFrame>
        {sheet.grid.preamble.length > 0 && (
          <SheetPreamble title={sheet.grid.preamble.join('\n')}>
            {sheet.grid.preamble.join(' · ')}
          </SheetPreamble>
        )}
        <EvidenceDataGrid
          rows={sheet.grid.rows}
          columns={sheet.grid.columns}
          initialState={FILE_ORDER}
        />
      </SheetFrame>
    )
  }
  return (
    <SheetMessageFrame>
      {sheet.grid ? (
        <NoResults inset margin="0" title="This sheet is empty" />
      ) : (
        <NoResults
          inset
          margin="0"
          title="This sheet can't be previewed"
          note="Download the file to view it."
          buttons={downloaderComponent}
        />
      )}
    </SheetMessageFrame>
  )
}

// One tab per sheet; a single-sheet workbook shows its grid alone.
const ExcelWorkbook = ({
  sheets,
  activeSheet,
  onSelectSheet,
  downloaderComponent,
  isModal,
  isExpanded,
}: {
  sheets: EvidenceExcelSheet[]
  activeSheet: string
  onSelectSheet: (key: string) => void
  downloaderComponent: JSX.Element
  isModal?: boolean
  isExpanded?: boolean
}): JSX.Element => {
  const sheetBody = (sheet: EvidenceExcelSheet) => {
    const body = (
      <ExcelSheet sheet={sheet} downloaderComponent={downloaderComponent} />
    )
    // The expanded view's modal frames the grid itself.
    return isExpanded ? (
      body
    ) : (
      <CsvDataGridContainer $isModal={isModal}>{body}</CsvDataGridContainer>
    )
  }
  if (sheets.length === 1) return sheetBody(sheets[0])
  return (
    <Tabs
      flush
      fillHeight
      attached
      activeKey={activeSheet}
      onSelect={(key) => key && onSelectSheet(key)}
      tabs={sheets.map((sheet, index) => ({
        title: sheet.hidden ? `${sheet.name} (hidden)` : sheet.name,
        tabKey: String(index),
        // A grid per sheet only once it is opened: workbooks can hold dozens.
        mountOnEnter: true,
        body: sheetBody(sheet),
      }))}
    />
  )
}

export const InlineExcel = ({
  sheets,
  downloaderComponent,
  isModalOpen,
  toggleModal,
  isModal,
}: {
  sheets: EvidenceExcelSheet[]
  downloaderComponent: JSX.Element
  isModalOpen: boolean
  toggleModal: () => void
  isModal?: boolean
}): JSX.Element => {
  // Shared, so Expand opens on the sheet shown inline.
  const [activeSheet, setActiveSheet] = useState('0')
  const workbookProps = {
    sheets,
    activeSheet,
    onSelectSheet: setActiveSheet,
    downloaderComponent,
  }
  return (
    <>
      <ExcelWorkbook {...workbookProps} isModal={isModal} />
      {isModalOpen && (
        <CsvPreviewModal
          body={<ExcelWorkbook {...workbookProps} isExpanded />}
          onClose={toggleModal}
        />
      )}
    </>
  )
}

export const InlineDocument = ({
  file,
  body,
  isEvidenceShowPage,
  pageNumber,
  downloaderComponent,
  isModal,
}: {
  file: File
  body?: Uint8Array | string
  isEvidenceShowPage?: boolean
  pageNumber?: number
  downloaderComponent: JSX.Element
  isModal?: boolean
}): JSX.Element => {
  const { isModalOpen, toggleModal } = useToggleDocumentViewer()
  const [link, setLink] = useState<string | null>(null)
  const FileTypeIcon = useFileTypeIcon({ mime: file.type })
  const { isImage, isPdf, isCsv, isExcel } = getFileType(file.type)

  useEffect(() => {
    const blob = new Blob([file], { type: file.type })
    const url = URL.createObjectURL(blob)
    setLink(url)

    return () => {
      URL.revokeObjectURL(url)
    }
  }, [file])

  const excelSheets: EvidenceExcelSheet[] | null = useMemo(() => {
    if (isExcel && body instanceof Uint8Array) {
      try {
        const sheets = getEvidenceExcel(body)
        // A workbook with no sheet to preview offers the download instead.
        return sheets.some((sheet) => sheet.grid) ? sheets : null
      } catch (error) {
        log.error('Error getting evidence excel', error)
        return null
      }
    }
    return null
  }, [isExcel, body])

  const showInlineExcel = useMemo(
    () => isExcel && !!excelSheets,
    [isExcel, excelSheets],
  )

  const showExpandButton = useMemo(() => {
    if (isExcel) {
      if (excelSheets) {
        return true
      }
      return false
    } else if (isEvidenceShowPage) {
      return true
    }
    return false
  }, [isExcel, excelSheets, isEvidenceShowPage])

  if ((!isImage && !isPdf && !isCsv && !isExcel) || !link) {
    return <></>
  }

  return (
    <>
      {showExpandButton && (
        <ExpandButtonRow>
          <TextButton onClick={toggleModal}>Expand</TextButton>
        </ExpandButtonRow>
      )}
      {isImage ? (
        <InlineImage
          file={file}
          isEvidenceShowPage={isEvidenceShowPage}
          link={link}
          isModalOpen={isModalOpen}
          toggleModal={toggleModal}
        />
      ) : isPdf ? (
        <InlinePdf
          link={link}
          isEvidenceShowPage={isEvidenceShowPage}
          isModalOpen={isModalOpen}
          toggleModal={toggleModal}
          pageNumber={pageNumber}
        />
      ) : isCsv ? (
        <InlineCsv
          body={body}
          isModalOpen={isModalOpen}
          toggleModal={toggleModal}
          isModal={isModal}
        />
      ) : isExcel ? (
        showInlineExcel ? (
          <InlineExcel
            sheets={excelSheets || []}
            downloaderComponent={downloaderComponent}
            isModalOpen={isModalOpen}
            toggleModal={toggleModal}
            isModal={isModal}
          />
        ) : (
          downloaderComponent
        )
      ) : (
        <PreviewDiv onClick={toggleModal}>
          <FileTypeIcon width="120px" height="120px" />
          <p>View Document</p>
        </PreviewDiv>
      )}
    </>
  )
}

export const CloseButton = ({
  onClick,
}: {
  onClick?: (e: React.MouseEvent<HTMLElement>) => void
}): JSX.Element => (
  <StyledCloseButton onClick={onClick}>close</StyledCloseButton>
)

export const CloseTextButton = ({
  onClick,
}: {
  onClick: (e: React.MouseEvent<HTMLElement>) => void
}): JSX.Element => (
  <StyledCloseButton onClick={onClick}>close</StyledCloseButton>
)

export const CsvPreviewModal = ({
  body,
  onClose,
}: {
  body: JSX.Element
  onClose: () => void
}): JSX.Element => {
  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    // Only close if clicking the backdrop itself, not its children
    if (e.target === e.currentTarget) {
      onClose()
    }
  }

  const handleContentClick = (e: React.MouseEvent<HTMLDivElement>) => {
    // Prevent clicks inside the content from bubbling to the backdrop
    e.preventDefault()
    e.stopPropagation()
  }

  return (
    <StyledCsvModal onClick={handleBackdropClick}>
      <StyledCsvBody onClick={handleContentClick}>{body}</StyledCsvBody>
      <CloseButton
        onClick={(e: React.MouseEvent<HTMLElement>) => {
          e.preventDefault()
          e.stopPropagation()
          onClose()
        }}
      />
    </StyledCsvModal>
  )
}
